import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useState, useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "../../assets/styles/colors.css";

function AIChat({
  chatHistory = [],
  chatMessage = "",
  loading = false,
  sessionLoading = false, // true while fetching a history session
  streamingStatus = null,
  streamingStatusHistory = [],
  iterationSnapshots = [],
  onMessageChange,
  onMessageSubmit,
  onReset,
  title,
  placeholder,
  emptyHistoryText,
  examplePrompt,
  examplePrompts = [],
  embedded = false,
  chatMode = "agent",
  onModeChange,
  onPlanOptionSelect,
  onOpenHistory,
  planActionLoading = false,
  planActionOption = null,
  onStop = null,
  onRevertToMessage = null,
  isRevertPreview = false,
  revertSourceIndex = null,
  onViewPlan = null,
}) {
  const { t } = useTranslation();
  const messagesEndRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const chatInputRef = useRef(null);
  const baseInputHeight = 44;
  const maxInputHeight = 160;

  const displayTitle = title || t("AI Chat", { defaultValue: "AI Chat" });
  const displayPlaceholder =
    placeholder ||
    t("Type your message...", { defaultValue: "Type your message..." });
  const displayEmptyHistory =
    emptyHistoryText ||
    t("Start a conversation", { defaultValue: "Start a conversation" });
  const displayExample = examplePrompt || null;
  const hasExamplePrompts =
    Array.isArray(examplePrompts) && examplePrompts.length > 0;

  const [expandedIterations, setExpandedIterations] = useState({});
  const toggleIteration = (key) =>
    setExpandedIterations((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (chatMessage.trim() && onMessageSubmit) {
      onMessageSubmit(e);
    }
  };

  const effectiveMode = chatMode || "agent";

  const handleHistoryClick = () => {
    onOpenHistory();
  };

  const containerClass = embedded
    ? "flex flex-col h-full min-h-0"
    : "w-full max-w-md rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-card)] p-3 shadow-[0_0_20px_var(--color-shadow-primary)] 2xl:w-80 flex flex-col";

  useEffect(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      if (messagesEndRef.current) {
        const chatContainer =
          messagesEndRef.current.closest(".flex-1.overflow-y-auto") ||
          messagesEndRef.current.closest('[class*="overflow-y-auto"]') ||
          messagesEndRef.current.parentElement?.parentElement;

        if (!chatContainer) {
          try {
            messagesEndRef.current.scrollIntoView({
              behavior: "smooth",
              block: "end",
            });
          } catch {
            messagesEndRef.current.scrollIntoView();
          }
          return;
        }

        const scrollBottom =
          chatContainer.scrollHeight -
          chatContainer.scrollTop -
          chatContainer.clientHeight;
        const isNearBottom = scrollBottom < 150;

        if (isNearBottom) {
          requestAnimationFrame(() => {
            if (messagesEndRef.current) {
              try {
                messagesEndRef.current.scrollIntoView({
                  behavior: "smooth",
                  block: "end",
                });
              } catch {
                messagesEndRef.current.scrollIntoView();
              }
            }
          });
        }
      }
    }, 100);

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [chatHistory]);

  useEffect(() => {
    if (!embedded) return;
    const textarea = chatInputRef.current;
    if (!textarea) return;
    const isEmpty = !(typeof chatMessage === "string" && chatMessage.trim());
    textarea.style.height = "0px";
    if (isEmpty) {
      textarea.style.height = `${baseInputHeight}px`;
      textarea.style.overflowY = "hidden";
      return;
    }
    const nextHeight = Math.min(textarea.scrollHeight, maxInputHeight);
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY =
      textarea.scrollHeight > maxInputHeight ? "auto" : "hidden";
  }, [chatMessage, embedded]);

  if (embedded) {
    return (
      <div className={containerClass} data-tour="ai-chat">
        {/* Header */}
        <div className="p-4 border-b border-[var(--color-border)] flex flex-wrap items-center justify-between gap-2 flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <svg
              className="w-5 h-5 text-[var(--color-primary)]"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M5 2.5a.75.75 0 01.72.51l.72 2.2a2.25 2.25 0 001.42 1.42l2.2.72a.75.75 0 010 1.42l-2.2.72a2.25 2.25 0 00-1.42 1.42l-.72 2.2a.75.75 0 01-1.42 0l-.72-2.2A2.25 2.25 0 002.09 9.35l-2.2-.72a.75.75 0 010-1.42l2.2-.72A2.25 2.25 0 004.29 5.2l.72-2.2A.75.75 0 015 2.5z" />
              <path d="M15 3.5a.75.75 0 01.72.51l.39 1.2c.2.61.67 1.08 1.28 1.28l1.2.39a.75.75 0 010 1.42l-1.2.39c-.61.2-1.08.67-1.28 1.28l-.39 1.2a.75.75 0 01-1.42 0l-.39-1.2a2.04 2.04 0 00-1.28-1.28l-1.2-.39a.75.75 0 010-1.42l1.2-.39c.61-.2 1.08-.67 1.28-1.28l.39-1.2A.75.75 0 0115 3.5z" />
              <path d="M12.5 12.5a.75.75 0 01.72.51l.55 1.7c.2.61.67 1.08 1.28 1.28l1.7.55a.75.75 0 010 1.42l-1.7.55a2.04 2.04 0 00-1.28 1.28l-.55 1.7a.75.75 0 01-1.42 0l-.55-1.7a2.04 2.04 0 00-1.28-1.28l-1.7-.55a.75.75 0 010-1.42l1.7-.55c.61-.2 1.08-.67 1.28-1.28l.55-1.7a.75.75 0 01.72-.51z" />
            </svg>
            <h2 className="font-bold text-sm uppercase tracking-wider text-slate-900 dark:text-[var(--color-text-white)]">
              {t(displayTitle)}
            </h2>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={handleHistoryClick} data-tour="chat-history-btn"
              className="flex items-center gap-1 px-1.5 py-0.5 sm:py-1 rounded-md border border-[var(--color-border)] text-[9px] sm:text-[10px] font-medium text-[var(--color-primary)] hover:bg-[var(--color-primary)]/10 transition-colors"
            >
              <svg
                className="w-3 h-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {t("topology.ai.history")}
            </button>
            {onReset && (
              <button
                type="button"
                onClick={onReset}
                className="p-1.5 hover:bg-[var(--color-primary)]/10 rounded-lg text-[var(--color-primary)] transition-colors"
                title={t("New Chat")} data-tour="chat-new-thread"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 4v16m8-8H4"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Mode selector */}
        {/* data-tour on mode selector wrapper added below */}
        {onModeChange && (
          <div className="px-4 py-3 border-b border-[var(--color-border)] flex-shrink-0">
            <div data-tour="chat-mode-selector" className="flex flex-nowrap gap-1 bg-slate-50 dark:bg-black/30 rounded-xl p-1 border border-slate-200 dark:border-[var(--color-border)]">
              {["ask", "agent", "plan"].map((key) => (
                <button
                  key={key} data-tour={`chat-mode-${key}`}
                  type="button"
                  onClick={() => onModeChange(key)}
                  className={`flex-1 min-w-0 px-2 py-1 text-[10px] sm:text-xs font-medium rounded-lg transition-all whitespace-nowrap ${effectiveMode === key ? "bg-[var(--color-primary)]/10 dark:bg-[var(--color-primary)]/20 text-[var(--color-primary)] border border-[var(--color-primary)]/40 shadow-sm" : "text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-gray-200"}`}
                >
                  {t(
                    key === "ask" ? "Ask" : key === "agent" ? "Agent" : "Plan",
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Messages area */}
        <div className="flex-1 overflow-y-auto min-h-0 topology-devices-scrollbar">
          {/* Session loading spinner — shown while fetching history */}
          {sessionLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-3 text-slate-400 dark:text-gray-500">
                <svg
                  className="w-6 h-6 animate-spin text-[var(--color-primary)]"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span className="text-xs font-medium">
                  {t("Loading chat...")}
                </span>
              </div>
            </div>
          ) : chatHistory.length > 0 ? (
            <div className="p-4 space-y-0">
              {chatHistory.map((m, idx) => (
                <div
                  key={idx}
                  data-message-item
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} mb-4 group/msg`}
                >
                  {m.role === "user" && m.checkpoint_id && !loading && (
                    <div className="flex flex-col gap-1 self-center mr-2 opacity-0 group-hover/msg:opacity-100 transition-opacity">
                      {onRevertToMessage && (
                        <button
                          onClick={() => onRevertToMessage(idx, m.checkpoint_id)}
                          className={`p-1 rounded ${
                            isRevertPreview && revertSourceIndex === idx
                              ? "!opacity-100 text-[var(--color-primary)] bg-[var(--color-primary)]/10"
                              : "text-gray-400 hover:text-[var(--color-primary)] hover:bg-gray-100 dark:hover:bg-gray-700"
                          }`}
                          title="View canvas at this point"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M7.707 3.293a1 1 0 010 1.414L5.414 7H11a7 7 0 017 7v2a1 1 0 11-2 0v-2a5 5 0 00-5-5H5.414l2.293 2.293a1 1 0 11-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                      )}

                    </div>
                  )}
                  <div
                    className={`max-w-[85%] px-4 py-2.5 rounded-xl text-sm leading-relaxed ${m.role === "user" ? `bg-[var(--color-primary)] text-white dark:text-black shadow-sm ${isRevertPreview && revertSourceIndex === idx ? "ring-2 ring-offset-2 ring-[var(--color-primary)]/60" : ""}` : "bg-slate-100 dark:bg-[var(--color-bg-overlay)] text-slate-900 dark:text-gray-200 border border-slate-200 dark:border-[var(--color-border)]"}`}
                  >
                    {m.role === "user" ? (
                      m.content
                    ) : (
                      <>
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            p: ({ children }) => (
                              <p className="mb-1 last:mb-0">{children}</p>
                            ),
                            strong: ({ children }) => (
                              <strong className="font-bold text-[var(--color-primary)]">
                                {children}
                              </strong>
                            ),
                            code: ({ children }) => (
                              <code className="bg-gray-200 dark:bg-black/50 px-1 py-0.5 rounded text-xs font-mono">
                                {children}
                              </code>
                            ),
                            pre: ({ children }) => (
                              <pre className="bg-gray-200 dark:bg-black/50 p-2 rounded my-1 overflow-x-auto text-[10px]">
                                {children}
                              </pre>
                            ),
                            ul: ({ children }) => (
                              <ul className="list-disc list-inside mb-1">
                                {children}
                              </ul>
                            ),
                            ol: ({ children }) => (
                              <ol className="list-decimal list-inside mb-1">
                                {children}
                              </ol>
                            ),
                            a: ({ href, children }) => (
                              <a
                                href={href}
                                className="text-[var(--color-primary)] underline"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {children}
                              </a>
                            ),
                            blockquote: ({ children }) => (
                              <blockquote className="border-l-2 border-[var(--color-primary)]/50 pl-2 my-1 italic">
                                {children}
                              </blockquote>
                            ),
                          }}
                        >
                          {m.content}
                        </ReactMarkdown>

                        {/* Plan options */}
                        {Array.isArray(m.planOptions) &&
                          m.planOptions.filter((o) => o !== "view").length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {m.planOptions.filter((o) => o !== "view").map((opt, oi) => {
                                const optionKey = String(
                                  opt || "",
                                ).toLowerCase();
                                const isActiveAction =
                                  planActionLoading &&
                                  optionKey ===
                                    String(
                                      planActionOption || "",
                                    ).toLowerCase();

                                return (
                                  <button
                                    key={oi}
                                    type="button"
                                    onClick={() =>
                                      onPlanOptionSelect
                                        ? onPlanOptionSelect(opt)
                                        : onMessageChange?.(opt)
                                    }
                                    disabled={planActionLoading}
                                    className={`px-2 py-1 rounded-md border border-[var(--color-border)] bg-transparent text-[10px] text-[var(--color-primary)] transition-colors ${planActionLoading ? "opacity-60 cursor-not-allowed" : "hover:bg-[var(--color-primary)]/10"}`}
                                  >
                                    {isActiveAction ? (
                                      <span className="inline-flex items-center gap-1">
                                        <svg
                                          className="w-3 h-3 animate-spin"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                        >
                                          <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                          />
                                          <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                          />
                                        </svg>
                                        <span>{opt}</span>
                                      </span>
                                    ) : (
                                      opt
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          )}

                        {/* View Plan button - only on assistant messages that have planOptions */}
                        {Array.isArray(m.planOptions) && m.planOptions.length > 0 && onViewPlan && (
                            <button
                              type="button"
                              onClick={() => onViewPlan()}
                              className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-[var(--color-border)] bg-transparent text-[10px] text-[var(--color-primary)] transition-colors hover:bg-[var(--color-primary)]/10"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                              </svg>
                              view plan
                            </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}

              {/* Streaming status */}
              {(streamingStatusHistory.length > 0 || streamingStatus) && (
                <div className="flex justify-start mb-4">
                  <div className="max-w-[85%] px-4 py-2.5 rounded-xl text-sm bg-slate-100 dark:bg-[var(--color-bg-overlay)] text-slate-600 dark:text-gray-400 border border-slate-200 dark:border-[var(--color-border)]">
                    {streamingStatusHistory.length > 0 ? (
                      <div className="space-y-1">
                        {streamingStatusHistory.map((status, idx) => {
                          const isLast =
                            idx === streamingStatusHistory.length - 1;
                          return (
                            <div
                              key={status.id || idx}
                              className={`flex items-center gap-2 text-xs ${isLast ? "" : "opacity-60"}`}
                            >
                              {isLast ? (
                                <svg
                                  className="w-3 h-3 animate-spin flex-shrink-0"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  />
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  />
                                </svg>
                              ) : (
                                <svg
                                  className="w-3 h-3 text-green-500 flex-shrink-0"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              )}
                              <span className={isLast ? "font-medium" : ""}>
                                {status.message || "Processing..."}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-4 h-4 animate-spin"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        <span>
                          {typeof streamingStatus === "string"
                            ? streamingStatus
                            : streamingStatus?.message || "Processing..."}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          ) : (
            // Empty state
            <div className="p-4 flex flex-col gap-4">
              <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-[var(--color-primary)]/10 dark:to-[var(--color-primary)]/5 p-4 rounded-xl border border-slate-200 dark:border-[var(--color-primary)]/20">
                <p className="text-sm leading-relaxed text-slate-700 dark:text-gray-200 font-medium">
                  {t("topology.ai.welcome")}
                </p>
              </div>
              {hasExamplePrompts && (
                <div className="space-y-2">
                  <p data-tour="chat-example-prompts" className="text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide px-1">
                    {t("Try asking")}
                  </p>
                  <div className="space-y-2">
                    {examplePrompts.map((prompt, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => onMessageChange?.(prompt)}
                        className="w-full text-left px-3 py-2.5 bg-white dark:bg-[var(--color-bg-overlay)] border border-slate-200 dark:border-[var(--color-border)] rounded-lg text-xs text-slate-600 dark:text-gray-300 hover:border-[var(--color-primary)] hover:bg-slate-50 dark:hover:bg-[var(--color-bg-overlay)]/80 transition-all group"
                      >
                        <span className="group-hover:text-[var(--color-primary)] transition-colors">
                          {prompt}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Input area */}
        <div className="p-4 border-t border-slate-200 dark:border-[var(--color-border)] bg-white dark:bg-[var(--color-bg-overlay)]/50 flex-shrink-0">
          <div className="relative flex items-end">
            <textarea
              ref={chatInputRef} data-tour="chat-input"
              value={chatMessage}
              onChange={(e) => onMessageChange?.(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
              placeholder={displayPlaceholder}
              rows={1}
              disabled={loading || sessionLoading}
              className="chat-input-scrollbar flex-1 pl-4 pr-12 py-2.5 bg-slate-50 dark:bg-[var(--color-bg-card)] border border-slate-200 dark:border-[var(--color-border)] rounded-lg text-[11px] sm:text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/40 focus:border-[var(--color-primary)] transition-all resize-none overflow-hidden text-slate-900 dark:text-[var(--color-text-white)] placeholder:text-[11px] sm:placeholder:text-sm placeholder:text-slate-400 dark:placeholder:text-gray-500"
            />

            {/* Stop button (while streaming) or Send button */}
            {loading && onStop ? (
              <button
                type="button"
                onClick={onStop}
                title="Stop generation"
                className="absolute right-1.5 bottom-1.5 p-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 active:scale-95 transition-all shadow-md hover:shadow-lg"
              >
                <svg
                  className="w-5 h-5"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <rect x="6" y="6" width="12" height="12" rx="2" />
                </svg>
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={
                  loading ||
                  sessionLoading ||
                  !(typeof chatMessage === "string" && chatMessage.trim())
                }
                className="absolute right-1.5 bottom-1.5 p-1.5 bg-[var(--color-primary)] text-white rounded-lg hover:bg-[var(--color-primary)]/85 active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 12h14m-7-7l7 7-7 7"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Non-embedded (legacy) variant
  return (
    <div className={containerClass} data-tour="ai-chat">
      <div className="mb-3 flex items-center justify-end gap-2 border-b border-[var(--color-border)] pb-2 flex-shrink-0">
        <div className="flex items-center gap-2">
          {onReset && (
            <button
              onClick={onReset}
              className="p-1 text-gray-400 hover:bg-[var(--color-primary)]/10"
              title={t("New chat")}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 4v16m8-8H4"
                />
              </svg>
            </button>
          )}
          <button
            onClick={handleHistoryClick} data-tour="chat-history-btn"
            className="p-1 text-gray-400 hover:bg-[var(--color-primary)]/10"
            title={t("Show history")}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <circle cx="12" cy="12" r="9" />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 7v5l3 3"
              />
            </svg>
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="p-2">
          {sessionLoading ? (
            <div className="flex items-center justify-center py-8 gap-2 text-gray-400">
              <svg
                className="w-4 h-4 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="text-sm">{t("Loading chat...")}</span>
            </div>
          ) : chatHistory.length === 0 ? (
            <div className="text-sm text-gray-400">
              {displayEmptyHistory}
              {displayExample && (
                <>
                  :{" "}
                  <code className="text-xs text-[var(--color-primary)]">
                    {displayExample}
                  </code>
                </>
              )}
            </div>
          ) : (
            <>
              {chatHistory.map((m, idx) => {
                const isUser = m.role === "user";
                return (
                  <div
                    key={idx}
                    data-message-item="true"
                    className={`mb-3 flex ${isUser ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      data-message-item="true"
                      className={`max-w-[90%] rounded-sm px-3 py-3 text-sm cursor-context-menu select-none ${
                        m.role === "user"
                          ? "bg-[var(--color-primary)] text-black"
                          : "bg-gray-100 text-gray-900"
                      }`}
                    >
                      {isUser ? (
                        m.content
                      ) : (
                        <>
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                              p: ({ children }) => (
                                <p className="mb-1 last:mb-0">{children}</p>
                              ),
                              strong: ({ children }) => (
                                <strong className="font-bold text-[var(--color-primary)]">
                                  {children}
                                </strong>
                              ),
                              em: ({ children }) => (
                                <em className="italic text-[var(--color-primary)]/80">
                                  {children}
                                </em>
                              ),
                              code: ({ children }) => (
                                <code className="bg-gray-200 px-1 py-0.5 rounded text-xs text-[var(--color-primary)] font-mono">
                                  {children}
                                </code>
                              ),
                              pre: ({ children }) => (
                                <pre className="bg-gray-200 p-2 rounded my-1 overflow-x-auto text-[10px] dark:bg-black/50">
                                  {children}
                                </pre>
                              ),
                              ul: ({ children }) => (
                                <ul className="list-disc list-inside mb-1 space-y-0.5">
                                  {children}
                                </ul>
                              ),
                              ol: ({ children }) => (
                                <ol className="list-decimal list-inside mb-1 space-y-0.5">
                                  {children}
                                </ol>
                              ),
                              li: ({ children }) => (
                                <li className="ml-1">{children}</li>
                              ),
                              a: ({ href, children }) => (
                                <a
                                  href={href}
                                  className="text-[var(--color-primary)] underline hover:text-[var(--color-primary)]/80"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {children}
                                </a>
                              ),
                              h1: ({ children }) => (
                                <h1 className="font-bold text-[var(--color-primary)] text-sm mb-1">
                                  {children}
                                </h1>
                              ),
                              h2: ({ children }) => (
                                <h2 className="font-bold text-[var(--color-primary)] text-xs mb-1">
                                  {children}
                                </h2>
                              ),
                              h3: ({ children }) => (
                                <h3 className="font-semibold text-[var(--color-primary)]/80 text-xs mb-1">
                                  {children}
                                </h3>
                              ),
                              blockquote: ({ children }) => (
                                <blockquote className="border-l-2 border-[var(--color-primary)]/50 pl-2 my-1 text-gray-400 italic">
                                  {children}
                                </blockquote>
                              ),
                            }}
                          >
                            {m.content}
                          </ReactMarkdown>

                          {Array.isArray(m.planOptions) &&
                            m.planOptions.filter((o) => o !== "view").length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-2">
                                {m.planOptions.filter((o) => o !== "view").map((option, optionIdx) => {
                                  const optionKey = String(
                                    option || "",
                                  ).toLowerCase();
                                  const isActiveAction =
                                    planActionLoading &&
                                    optionKey ===
                                      String(
                                        planActionOption || "",
                                      ).toLowerCase();

                                  return (
                                    <button
                                      key={`${idx}-plan-option-${optionIdx}`}
                                      type="button"
                                      onClick={() => {
                                        if (onPlanOptionSelect) {
                                          onPlanOptionSelect(option);
                                        } else {
                                          onMessageChange?.(option);
                                        }
                                      }}
                                      disabled={planActionLoading}
                                      className={`px-2 py-1 rounded-full border border-[var(--color-border)] bg-transparent text-[10px] text-[var(--color-primary)] transition-colors ${planActionLoading ? "opacity-60 cursor-not-allowed" : "hover:bg-[var(--color-primary)]/10"}`}
                                    >
                                      {isActiveAction ? (
                                        <span className="inline-flex items-center gap-1">
                                          <svg
                                            className="w-3 h-3 animate-spin"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                          >
                                            <circle
                                              className="opacity-25"
                                              cx="12"
                                              cy="12"
                                              r="10"
                                              stroke="currentColor"
                                              strokeWidth="4"
                                            />
                                            <path
                                              className="opacity-75"
                                              fill="currentColor"
                                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            />
                                          </svg>
                                          <span>{option}</span>
                                        </span>
                                      ) : (
                                        option
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}

              {(streamingStatusHistory.length > 0 || streamingStatus) && (
                <div className="mb-3 flex justify-start">
                  <div className="max-w-[90%] rounded-sm px-3 py-3 text-sm bg-gray-100 text-gray-600">
                    {streamingStatusHistory.length > 0 ? (
                      <div className="space-y-1">
                        {streamingStatusHistory.map((status, idx) => {
                          const isLast =
                            idx === streamingStatusHistory.length - 1;
                          return (
                            <div
                              key={status.id || idx}
                              className={`flex items-center gap-2 text-xs ${isLast ? "" : "opacity-60"}`}
                            >
                              {isLast ? (
                                <svg
                                  className="w-3 h-3 animate-spin flex-shrink-0"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  />
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  />
                                </svg>
                              ) : (
                                <svg
                                  className="w-3 h-3 text-green-500 flex-shrink-0"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              )}
                              <span className={isLast ? "font-medium" : ""}>
                                {status.message || "Processing..."}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-3 h-3 animate-spin"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        <span>
                          {typeof streamingStatus === "string"
                            ? streamingStatus
                            : streamingStatus?.message || "Processing..."}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>
      </div>

      {/* Chat mode selector */}
      <div className="mt-1 mb-1 flex justify-center gap-1 text-[10px]">
        {[
          { key: "ask", label: "Ask" },
          { key: "agent", label: "Agent" },
          { key: "plan", label: "Plan" },
        ].map((modeOption) => {
          const isActive = effectiveMode === modeOption.key;
          return (
            <button
              key={modeOption.key}
              type="button"
              onClick={() => onModeChange?.(modeOption.key)}
              className={
                isActive
                  ? "px-2 py-1 rounded-full bg-[var(--color-primary)] text-white dark:text-black font-semibold shadow-sm"
                  : "px-2 py-1 rounded-full border border-[var(--color-border)] text-[var(--color-text-muted)] bg-transparent hover:bg-[var(--color-primary-50)]"
              }
            >
              {modeOption.label}
            </button>
          );
        })}
      </div>

      {/* Chat input */}
      <form
        onSubmit={handleSubmit}
        className="flex items-center gap-2 pt-3 mt-auto shrink-0 backdrop-blur-sm px-2"
      >
        <input
          type="text"
          value={chatMessage}
          disabled={loading || sessionLoading}
          onChange={(e) => onMessageChange?.(e.target.value)}
          placeholder={displayPlaceholder}
          className="h-8 rounded-sm flex-1 border border-[var(--color-border)] bg-[var(--color-bg)] px-3 text-sm text-[var(--color-text-white)] placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
        />
        <button
          type="submit"
          disabled={loading || sessionLoading || !chatMessage.trim()}
          className={`h-8 w-8 rounded-sm flex items-center justify-center shrink-0 ${
            loading
              ? "bg-gray-100 text-[var(--color-primary)]"
              : "bg-[var(--color-primary)] text-black hover:shadow-[0_0_8px_var(--color-shadow-primary-hover)]"
          } disabled:cursor-default disabled:bg-gray-100 disabled:text-[var(--color-primary)]/60 disabled:opacity-60`}
          title={loading ? t("Thinking...") : t("Send")}
        >
          {loading ? (
            <svg
              className="w-4 h-4 animate-spin"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          ) : (
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13 5l7 7-7 7M5 5l7 7-7 7"
              />
            </svg>
          )}
        </button>
      </form>
    </div>
  );
}

AIChat.propTypes = {
  chatHistory: PropTypes.arrayOf(
    PropTypes.shape({
      role: PropTypes.string.isRequired,
      content: PropTypes.string.isRequired,
    }),
  ),
  chatMessage: PropTypes.string,
  loading: PropTypes.bool,
  sessionLoading: PropTypes.bool,
  streamingStatus: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      phase: PropTypes.string,
      message: PropTypes.string,
    }),
  ]),
  streamingStatusHistory: PropTypes.arrayOf(
    PropTypes.shape({
      phase: PropTypes.string,
      message: PropTypes.string,
      node: PropTypes.string,
      timestamp: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  iterationSnapshots: PropTypes.arrayOf(
    PropTypes.shape({
      iteration: PropTypes.number,
      maxIterations: PropTypes.number,
      valid: PropTypes.bool,
      reason: PropTypes.string,
      nodeCount: PropTypes.number,
      edgeCount: PropTypes.number,
      otFindings: PropTypes.array,
    }),
  ),
  onMessageChange: PropTypes.func,
  onMessageSubmit: PropTypes.func,
  onReset: PropTypes.func,
  title: PropTypes.string,
  placeholder: PropTypes.string,
  emptyHistoryText: PropTypes.string,
  examplePrompt: PropTypes.string,
  examplePrompts: PropTypes.arrayOf(PropTypes.string),
  embedded: PropTypes.bool,
  chatMode: PropTypes.oneOf(["ask", "agent", "plan"]),
  onModeChange: PropTypes.func,
  onPlanOptionSelect: PropTypes.func,
  onOpenHistory: PropTypes.func.isRequired,
  planActionLoading: PropTypes.bool,
  planActionOption: PropTypes.string,
  onStop: PropTypes.func,
  onRevertToMessage: PropTypes.func,
  onViewPlan: PropTypes.func,
  isRevertPreview: PropTypes.bool,
  revertSourceIndex: PropTypes.number,
};

export default AIChat;